import api from './api';

export interface MftFile {
  name: string;
  size: number;
  is_dir: boolean; // Edge API uses is_dir, not is_directory
  mod_time?: string;
  mode?: string;
}

export interface MftListResponse {
  files: MftFile[];
  path: string;
  status: string;
}

export interface AuthCredentials {
  username: string;
  password: string;
}

export interface OTPSetupResult {
  secret: string;
  qr_code_url: string;
  backup_codes: string[];
}

export interface LoginResult {
  requires_otp: boolean;
  session_token?: string;
  token?: string;
  expires_at: string;
  user: {
    username: string;
  };
}

class EdgeMftService {
  async authenticate(credentials: AuthCredentials): Promise<LoginResult> {
    try {
      // Call the new login API
      const response = await api.post('/auth/login', credentials);
      const data: LoginResult = response.data;

      // If OTP not required, store token immediately
      if (!data.requires_otp && data.token) {
        localStorage.setItem('mft-token', data.token);
        localStorage.setItem('mft-user', JSON.stringify(data.user));
        localStorage.setItem('mft-expires', data.expires_at);
      }

      return data;
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  async verifyOTP(sessionToken: string, otpCode: string): Promise<boolean> {
    try {
      const response = await api.post('/auth/verify-otp', {
        session_token: sessionToken,
        code: otpCode,
      });
      const { token, expires_at, user } = response.data;

      // Store token after successful OTP verification
      localStorage.setItem('mft-token', token);
      localStorage.setItem('mft-user', JSON.stringify(user));
      localStorage.setItem('mft-expires', expires_at);

      return true;
    } catch (error) {
      throw error;
    }
  }

  async setupOTP(username: string): Promise<OTPSetupResult> {
    const response = await api.post('/auth/otp/setup', { username });
    return response.data;
  }

  async verifyOTPSetup(username: string, secret: string, code: string): Promise<void> {
    await api.post('/auth/otp/verify-setup', {
      username,
      secret,
      code,
    });
  }

  async enableOTP(username: string, secret: string, backupCodes: string[]): Promise<void> {
    await api.post('/auth/otp/enable', {
      username,
      secret,
      backup_codes: backupCodes,
    });
  }

  async disableOTP(username: string): Promise<void> {
    await api.post('/auth/otp/disable', {
      username,
    });
  }

  async listFiles(path: string = '/'): Promise<MftFile[]> {
    const response = await api.get(`/list${path}`);
    const data: MftListResponse = response.data;
    return data.files || [];
  }

  async uploadFile(file: File, path: string = '/'): Promise<void> {
    const formData = new FormData();
    formData.append('path', path);
    formData.append('file', file);

    await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async downloadFile(path: string): Promise<Blob> {
    const response = await api.get(`/download${path}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteFile(path: string): Promise<void> {
    await api.delete(`/delete${path}`);
  }

  async createDirectory(path: string): Promise<void> {
    await api.post('/mkdir', { path });
  }

  async logout(): Promise<void> {
    try {
      // Call logout API to invalidate server-side session
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with local cleanup even if server call fails
    } finally {
      // Clear local storage
      localStorage.removeItem('mft-token');
      localStorage.removeItem('mft-user');
      localStorage.removeItem('mft-expires');
    }
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('mft-token');
    const expires = localStorage.getItem('mft-expires');
    
    if (!token || !expires) {
      return false;
    }

    // Check if token is expired
    const expiryDate = new Date(expires);
    if (new Date() > expiryDate) {
      this.logout();
      return false;
    }

    return true;
  }

  getCredentials(): AuthCredentials | null {
    const user = localStorage.getItem('mft-user');
    if (user) {
      const userData = JSON.parse(user);
      // Return in the expected AuthCredentials format for compatibility
      return { username: userData.username, password: '' };
    }
    return null;
  }

  getToken(): string | null {
    return localStorage.getItem('mft-token');
  }

  async getUserInfo(): Promise<{ username: string; expires_at: string; otp_enabled: boolean; otp_verified: boolean }> {
    const response = await api.get('/auth/me');
    return response.data;
  }
}

export default new EdgeMftService();